import { useMemo } from 'react'
import { calculateAnalysis } from '../lib/analysis-utils'
import { type MarketStore, useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'
import { AnalysisMode, type OptionData } from '../types'

/**
 * Standardized hook for market analysis across the application.
 * Handles base-snapshot selection, delta calculation, and final analysis.
 */
export const useAnalysis = () => {
    const symbol = useSettingsStore((state) => state.symbol)
    const mode = useSettingsStore((state) => state.mode)
    const range = useSettingsStore((state) => state.range)
    const expiry = useSettingsStore((state) => state.expiry)
    
    const marketData = useMarketStore((s: MarketStore) => s.fullData[symbol])

    // Placeholder for Base Snapshot logic (Future V4 Feature)
    // In V4 transition, we focus on Live Mode first.
    const baseSnapshot = null
    const analysis = null // Analysis caching if needed

    return useMemo(() => {
        if (!marketData || !marketData.options) return null

        // 1. Extract Base Options from Centralized Locked Snapshot (If implemented)
        const baseOptions: any[] = []
        if (baseSnapshot) {
            // TODO: Implement Base Snapshot Retrieval
        }

        const currentOptions = marketData.options
        const spotPrice = marketData.spotPrice || (currentOptions.length > 0 ? currentOptions[0]?.strike || 0 : 0)
        if (!spotPrice) return null

        // 3. Calculate Change Percentages (Deltas) relative to Base
        // If baseOptions exist, calculate delta. Otherwise use current (Live Day Change).
        let optionsToAnalyze = currentOptions

        if (baseOptions.length > 0) {
            const baseMap = new Map()
            baseOptions.forEach((opt) => {
                baseMap.set(opt.strike, opt)
            })

            optionsToAnalyze = currentOptions.map((curr: OptionData) => {
                const base = baseMap.get(curr.strike)
                if (!base) return curr

                const ceVal = mode === AnalysisMode.OI ? curr.callOpenInterest : curr.callVolume
                const peVal = mode === AnalysisMode.OI ? curr.putOpenInterest : curr.putVolume

                let baseCeVal = mode === AnalysisMode.OI ? base.callOpenInterest : base.callVolume
                let basePeVal = mode === AnalysisMode.OI ? base.putOpenInterest : base.putVolume

                // Prevent division by zero
                if (baseCeVal <= 0) baseCeVal = 1
                if (basePeVal <= 0) basePeVal = 1

                return {
                    ...curr,
                    callChangePercent: ((ceVal - baseCeVal) / baseCeVal) * 100,
                    putChangePercent: ((peVal - basePeVal) / basePeVal) * 100,
                }
            })
        }

        // 4. Run Core Analysis Algorithm
        return calculateAnalysis(
            optionsToAnalyze,
            Number(range),
            spotPrice,
            mode,
            symbol,
            baseOptions.length > 0 ? baseOptions : undefined,
        )
    }, [marketData, symbol, range, mode, expiry, baseSnapshot, analysis])
}
